import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import {
	AppFeature,
	ComplexAppFeature,
	ComplexFeatureSettings,
	Feature,
	isComplexFeature
} from '../../types/check/flags';
import { initStoreState } from '../actions';

type FeaturesState = {
	activated: Record<AppFeature, boolean>;
	settings: {
		[Feature in ComplexAppFeature]?: ComplexFeatureSettings<Feature>;
	};
};

const initialState: FeaturesState = {
	activated: {
		[AppFeature.ASSESSMENT_USER_INPUT]: false,
		[AppFeature.EDIT_CHECK_DATA]: false,
		[AppFeature.DEACTIVATE_CONSENT_FORWARDING_AND_RESULTS]: false,
		[AppFeature.SHOW_RESULTS]: false,
		[AppFeature.CONSENT_ADDITIONAL_1]: false,
		[AppFeature.PLACE_OF_RESIDENCE]: false,
		[AppFeature.CONSENT_ADDITIONAL_2]: false,
		[AppFeature.DOB]: false
	},
	settings: {}
};

const featuresSlice = createSlice({
	name: 'features',
	initialState,
	reducers: {
		setFeatures: (state, action: PayloadAction<Array<Feature>>) => {
			const features = action.payload;

			features.forEach((feature: Feature) => {
				state.activated[feature.name] = true;
				if (isComplexFeature(feature)) {
					state.settings[feature.name] = feature.settings;
				}
			});
		},
		resetFeatures() {
			return initialState;
		}
	},
	extraReducers: (builder) => {
		builder.addCase(initStoreState, (state, action) => {
			const check = action.payload;
			const features = check.flags;

			features.forEach((feature: Feature) => {
				state.activated[feature.name] = true;
				if (isComplexFeature(feature)) {
					state.settings[feature.name] = feature.settings;
				}
			});
		});
	}
});

export const selectFeature = (state: RootState, feature: AppFeature): boolean => {
	return state.features.activated[feature];
};

export const selectFeatureSettings = <Feature extends ComplexAppFeature>(
	state: RootState,
	feature: Feature
): ComplexFeatureSettings<Feature> | undefined => {
	return state.features.settings[feature];
};

export const selectDeactivatedConsentForwardingAndResults = (state: RootState) =>
	selectFeature(state, AppFeature.DEACTIVATE_CONSENT_FORWARDING_AND_RESULTS);

export const { setFeatures, resetFeatures } = featuresSlice.actions;

export default featuresSlice;
